import { Typography } from '@mui/material'
import BlockList from 'components/core/block-list'
import { APIError } from 'components/core/errors/api'
import { SafeMarkdown } from 'components/core/markdown'
import { CommentRowContainer } from 'components/pages/task/audit/table'
import { dateSort } from 'components/pages/task/details/task-content-v2/common/TaskUtil'
import { UserValue } from 'lib/actions/user/index'
import { TaskAuditListRequest, TaskAuditView } from 'pbts/c1/api/task/v1/audit'
import { FC, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { TaskAuditApi } from 'redux_toolkit/tasks'

type CommentListV2Props = {
    taskId: string
}

const CommentRow: FC<{ audit: TaskAuditView }> = ({ audit }) => {
    const user: UserValue | undefined = audit?.metadata?.user
    if (!audit || !user || audit.typ.oneofKind !== 'comment') {
        return null
    }

    return <CommentRowContainer audit={audit} user={user} role="article">
        <Typography>
            <SafeMarkdown>{audit.typ.comment?.comment || ''}</SafeMarkdown>
        </Typography>
    </CommentRowContainer>
}

export const CommentListV2: FC<CommentListV2Props> = ({ taskId }) => {
    const [comments, setComments] = useState<TaskAuditView[]>([])

    const {
        data: audit,
        isLoading,
        isError,
        error,
    } = TaskAuditApi.useList(TaskAuditListRequest.create({ taskId }), { notify: true, autoReload: true })

    useEffect(() => {
        if (isLoading || isError) {
            return
        }

        const auditList = audit.reduce((prev, next) => {
            return prev.concat(next)
        })
        setComments(
            auditList
                .slice()
                .sort(dateSort)
                .filter((item) => item.typ.oneofKind === 'comment'),
        )
    }, [audit, isLoading, isError])

    if (isError && error) {
        return <APIError message={error.message} code={error.code} />
    }

    return <BlockList<TaskAuditView>
        emptyText={
            <Typography variant="unconfigured">
                <FormattedMessage
                    id="components_task_review_details_comments_empty_text"
                    defaultMessage="There are no comments on this task"
                />
            </Typography>
        }
        items={comments}
        renderItem={(auditView: TaskAuditView): React.ReactElement => <CommentRow audit={auditView} />}
    />
}
